import React, { useCallback, useEffect, useMemo } from 'react';

import { ICON_TYPES, ICONS } from '@/src/components/common/Elements/Icon/Icon';
import useConfirmPopup from '@/src/components/common/Elements/Modal/useConfirmPopup';
import SectionLoader from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import { FileEntry } from '@/src/components/project/Inbox2/types';
import { showToast } from '@/src/globalFunction';
import { queryClient } from '@/src/lib/react-query/react-query';

import { useCancelAutomation } from './api/cancelAutomation';
import { useCreateAutomation } from './api/createAutomation';
import { GetAutomationResultResponse, useGetAutomationResult } from './api/getAutomationResult';
import { GetDraftsResponse, treatmentDraftsQueryKey } from './api/getDrafts';
import AutomationProcessingView from './components/Automation/AutomationProcessingView';
import TreatmentDisplay from './components/TreatmentDisplay/TreatmentDisplay';
import { BaseFormOption } from './components/TreatmentForm/BaseForm/BaseForm';
import { CheckPaymentFormOption } from './components/TreatmentForm/CheckPaymentForm/CheckPaymentForm';
import { PaidBillFormOption } from './components/TreatmentForm/PaidBillForm/PaidBillForm';
import TreatmentForm from './components/TreatmentForm/TreatmentForm';
import { UnpaidBillFormOption } from './components/TreatmentForm/UnpaidBillForm/UnpaidBillForm';
import TreatmentList from './components/TreatmentList/TreatmentList';
import { mapDraftData } from './Helper';
import { useTreatments } from './store/treatments.store';

import { refreshInbox } from '../../../../../AllView/components/Header/refreshInbox';

export type FullScreenSetting = {
  isContainerFullScreen: boolean;
  isViewerFullScreen: boolean;
};

type TreatmentProps = {
  fileEntry: FileEntry;
};

const Treatment = ({ fileEntry }: TreatmentProps) => {
  const {
    form,
    treatment,
    draft,
    automation,
    isViewable,
    isViewInQuickbooksEnabled,
    setForm,
    setTreatment,
    setDraft,
    addToAutomation,
    removeFromAutomation,
    onPublished,
    onReset,
    onDraftAction,
    onUpdateDraft,
    initializeDraftFromAutomation,
  } = useTreatments();

  const FORM_TYPES = useMemo(() => {
    return {
      Bill: UnpaidBillFormOption,
      Expense: PaidBillFormOption,
      Check: CheckPaymentFormOption,
      Base: BaseFormOption,
    };
  }, []);

  const [ConfirmAutomationErrorModal, confirmAutomationError] = useConfirmPopup({
    title: (
      <span className="tw-text-cfo-blue-600 tw-font-brandonTextMedium tw-text-[18px]">
        Changes have been made to this entry
      </span>
    ),
    message: `Please reload the page to see the latest updates before proceeding`,
    cancelBtn: {
      label: 'Cancel',
    },
    confirmBtn: {
      label: 'Reload the page',
    },
    icon: {
      icon: ICONS.info,
      iconType: ICON_TYPES.outlined,
      className: 'tw-text-cfo-blue-600 tw-text-[22px] tw-font-medium ',
    },
  });

  const documentId = fileEntry.file.id;
  const inboxDocumentId = fileEntry.id;

  //automation
  const automationId = automation?.[documentId] || null;
  const hasDocumentAutomation = !!automationId;

  const automationMutation = useCreateAutomation({
    inboxDocumentId,
  });

  const cancelAutomationMutation = useCancelAutomation({
    automationId,
  });

  const automationQuery = useGetAutomationResult({
    automationId,
    inboxDocumentId,
    config: {
      enabled: hasDocumentAutomation,
      refetchInterval: (data: GetAutomationResultResponse) => {
        return data?.status === 'processing' ? 5000 : false;
      },
    },
  });

  const isFailedAutomation = useMemo(() => {
    return (
      !!automationQuery.error ||
      automationQuery?.data?.status === 'cancelled' ||
      automationQuery?.data?.status === 'failed' ||
      (automationQuery?.data?.status === 'completed' && !automationQuery?.data?.draft)
    );
  }, [automationQuery?.data, automationQuery.error]);

  // automation completed
  useEffect(() => {
    const automationData = automationQuery.data;
    if (!automationData || !automationData.draft || automationData?.status === 'processing') return;

    initializeDraftFromAutomation({
      draft: mapDraftData(automationData.draft),
      form: FORM_TYPES[automationData.draft.type] || FORM_TYPES['Base'],
      fileId: fileEntry.file.id,
    });

    queryClient.setQueryData(
      [...treatmentDraftsQueryKey(documentId)],
      (prev: GetDraftsResponse) => {
        return [automationData.draft, ...prev];
      }
    );
  }, [
    FORM_TYPES,
    automationQuery.data,
    documentId,
    initializeDraftFromAutomation,
    removeFromAutomation,
    fileEntry,
  ]);

  // automation mutation has error
  useEffect(() => {
    if (!automationMutation.error) {
      return;
    }

    (async function () {
      if (!(await confirmAutomationError())) {
        return;
      }

      refreshInbox();
    })();
  }, [automationMutation.error, confirmAutomationError]);

  // hide failed automation banner on create treatment, successful automation
  useEffect(() => {
    if (!!form && !draft && isFailedAutomation) {
      removeFromAutomation(documentId);
    }
  }, [documentId, draft, form, isFailedAutomation, removeFromAutomation]);

  const handleOnPublished = useCallback(
    (showSuccessToast = false) => {
      onPublished();

      if (showSuccessToast) {
        showToast('success', 'Entry has been successfully published!');
      }
    },
    [onPublished]
  );

  const handleOnCancelAutomation = () => {
    cancelAutomationMutation.mutateAsync();
  };

  const handleCloseAutomationBanner = () => {
    // cancelAutomationMutation.reset();
    removeFromAutomation(documentId);
  };

  const handleClickAutomate = async () => {
    cancelAutomationMutation.reset();
    const automateResponse = await automationMutation.mutateAsync();
    addToAutomation({ [documentId]: automateResponse.automationId });
  };

  if (
    (cancelAutomationMutation.isLoading || cancelAutomationMutation?.data?.success) &&
    automationQuery?.data?.status === 'processing'
  ) {
    return <SectionLoader />;
  }

  return (
    <>
      {!form && !treatment && automationQuery.data?.status !== 'processing' && (
        <TreatmentList
          isFailedAutomation={isFailedAutomation}
          fileEntry={fileEntry}
          setTreatment={setTreatment}
          setForm={setForm}
          setDraft={setDraft}
          isViewable={isViewable}
          onCloseFailedAutomationBanner={handleCloseAutomationBanner}
          onClickAutomate={handleClickAutomate}
        />
      )}
      {treatment && (
        <TreatmentDisplay
          treatment={treatment}
          onBack={onReset}
          isViewInQuickbooksEnabled={isViewInQuickbooksEnabled}
        />
      )}
      {form && (
        <TreatmentForm
          form={form}
          draft={draft}
          documentId={documentId}
          onPublished={handleOnPublished}
          setTreatment={setTreatment}
          onBack={onReset}
          setForm={setForm}
          onUpdateDraft={onUpdateDraft}
          onDraftAction={onDraftAction}
          removeFromAutomation={removeFromAutomation}
        />
      )}
      {hasDocumentAutomation && automationQuery.data?.status === 'processing' && (
        <AutomationProcessingView onCancelAutomation={handleOnCancelAutomation} />
      )}
      <ConfirmAutomationErrorModal />
    </>
  );
};

export default Treatment;
